import type { VerifiedSlip } from '@pf/contracts';
import { Braces, ImageOff, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { bankInfo } from '@/lib/banks';
import { formatDateTime } from '@/lib/datetime';
import { formatMoney } from '@/lib/money';
import { cn, clamp } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Code } from '@/components/ui/primitives';

/**
 * The slip viewer.
 *
 * There is no slip *image* to show. That is not a shortcut — it is the architecture: SAD §6 says
 * the input to verification is "the slip's QR payload (preferred) or image", and what comes back
 * from the provider is the canonical `VerifiedSlip`, which is **bank-attested data**, not a
 * picture. The raw upload lives in encrypted object storage under a per-participant key (SAD §11)
 * and deliberately never reaches the console.
 *
 * So this component renders the attested record *as* a slip: the same fields, in the same layout
 * a Thai bank produces, with the bank's own branding. That is more useful than the photograph
 * would be — a photograph can be forged, and the whole reason SlipOK exists is that this data
 * cannot. The QR block is drawn deterministically from the `trans_ref`, so the same slip always
 * looks the same and two different slips never look alike.
 *
 * Zoom and rotate are here because operators reviewing slips do both reflexively, and a viewer
 * that cannot be zoomed is a viewer they will screenshot into another tool.
 */

/** Props for {@link SlipViewer}. */
export interface SlipViewerProps {
  /** The provider's canonical result, or `null` when verification never returned one. */
  slip: VerifiedSlip | null;
  /** Additional classes. */
  className?: string | undefined;
}

/** Deterministic 21×21 QR-like matrix derived from a transaction reference. */
function qrMatrix(seed: string): boolean[][] {
  const size = 21;
  let hash = 2166136261;
  for (const ch of seed) {
    hash ^= ch.charCodeAt(0);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  const cells: boolean[][] = [];
  for (let y = 0; y < size; y += 1) {
    const row: boolean[] = [];
    for (let x = 0; x < size; x += 1) {
      // Finder patterns in three corners, exactly where a real QR has them.
      const inFinder = (x < 7 && y < 7) || (x >= size - 7 && y < 7) || (x < 7 && y >= size - 7);
      if (inFinder) {
        const fx = x < 7 ? x : x - (size - 7);
        const fy = y < 7 ? y : y - (size - 7);
        const ring = Math.max(Math.abs(fx - 3), Math.abs(fy - 3));
        row.push(ring !== 2 && ring <= 3);
        continue;
      }
      hash ^= (x * 31 + y * 17) >>> 0;
      hash = Math.imul(hash, 16777619) >>> 0;
      row.push(((hash >>> 8) & 1) === 1);
    }
    cells.push(row);
  }
  return cells;
}

/** A deterministic QR-code-like graphic. */
function QrBlock({ seed }: { seed: string }): ReactNode {
  const { t } = useTranslation();
  const cells = qrMatrix(seed);
  return (
    <svg
      viewBox="0 0 21 21"
      className="size-20 shrink-0 rounded bg-white p-1 shadow-sm"
      role="img"
      aria-label={t('domain.slipViewer.qrAriaLabel')}
    >
      {cells.map((row, y) =>
        row.map((on, x) =>
          on ? (
            <rect
              key={`${String(x)}-${String(y)}`}
              x={x}
              y={y}
              width="1"
              height="1"
              fill="#0f172a"
            />
          ) : null,
        ),
      )}
    </svg>
  );
}

/** Render a verified slip. */
export function SlipViewer({ slip, className }: SlipViewerProps): ReactNode {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [showRaw, setShowRaw] = useState(false);

  if (!slip) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-grid px-6 py-14 text-center',
          className,
        )}
      >
        <div className="flex size-11 items-center justify-center rounded-full border border-border bg-background text-muted-foreground">
          <ImageOff className="size-5" aria-hidden="true" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-semibold">{t('domain.slipViewer.noSlipTitle')}</p>
          <p className="mx-auto max-w-sm text-sm leading-relaxed text-muted-foreground">
            {t('domain.slipViewer.noSlipDescription')}
          </p>
        </div>
      </div>
    );
  }

  const bank = bankInfo(slip.receiver.bank);

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-2xs uppercase tracking-wide text-muted-foreground">
          {t('domain.slipViewer.bankAttestedRecordPrefix')}{' '}
          <span className="font-semibold">{slip.provider}</span>
        </p>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('domain.slipViewer.zoomOutAriaLabel')}
            onClick={() => {
              setZoom((z) => clamp(Number((z - 0.15).toFixed(2)), 0.7, 2));
            }}
          >
            <ZoomOut aria-hidden="true" />
          </Button>
          <span className="tnum w-12 text-center text-xs text-muted-foreground">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('domain.slipViewer.zoomInAriaLabel')}
            onClick={() => {
              setZoom((z) => clamp(Number((z + 0.15).toFixed(2)), 0.7, 2));
            }}
          >
            <ZoomIn aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('domain.slipViewer.rotateAriaLabel')}
            onClick={() => {
              setRotation((r) => (r + 90) % 360);
            }}
          >
            <RotateCw aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={
              showRaw
                ? t('domain.slipViewer.showSlip')
                : t('domain.slipViewer.showRawProviderResponse')
            }
            aria-pressed={showRaw}
            onClick={() => {
              setShowRaw((v) => !v);
            }}
          >
            <Braces aria-hidden="true" />
          </Button>
        </div>
      </div>

      {showRaw ? (
        <pre className="max-h-96 overflow-auto rounded-lg border border-border bg-muted/40 p-4 text-xs leading-relaxed">
          <code>{JSON.stringify(slip, null, 2)}</code>
        </pre>
      ) : (
        <div className="overflow-auto rounded-lg border border-border bg-muted/30 p-6">
          <div
            className="mx-auto w-full max-w-sm origin-top transition-transform"
            style={{ transform: `scale(${String(zoom)}) rotate(${String(rotation)}deg)` }}
          >
            <article className="overflow-hidden rounded-xl bg-white text-slate-900 shadow-lg ring-1 ring-black/5">
              <header
                className="flex items-center gap-3 px-5 py-4 text-white"
                style={{ backgroundColor: bank.color }}
              >
                <span className="flex size-9 items-center justify-center rounded-lg bg-white/15 text-sm font-bold">
                  {bank.monogram}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold">{bank.name}</span>
                  <span className="block truncate text-xs opacity-90">{bank.nameTh}</span>
                </span>
              </header>

              <div className="space-y-4 px-5 py-5">
                <div className="text-center">
                  <p className="text-xs font-medium text-emerald-700">
                    โอนเงินสำเร็จ · Transfer successful
                  </p>
                  <p className="tnum pt-1 text-3xl font-bold">
                    {formatMoney(slip.amount, { withSymbol: true })}
                  </p>
                  <p className="text-xs text-slate-500">{slip.amount.currency}</p>
                </div>

                <dl className="space-y-2.5 border-t border-slate-200 pt-4 text-xs">
                  <div className="flex justify-between gap-4">
                    <dt className="shrink-0 text-slate-500">ผู้รับ / To</dt>
                    <dd className="text-right font-medium">{slip.receiver.name}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="shrink-0 text-slate-500">ธนาคาร / Bank</dt>
                    <dd className="text-right font-medium">{slip.receiver.bank}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="shrink-0 text-slate-500">เลขที่บัญชี / Account</dt>
                    <dd className="tnum text-right font-medium">{slip.receiver.account_masked}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="shrink-0 text-slate-500">วันที่ / Date</dt>
                    <dd className="tnum text-right font-medium">
                      {formatDateTime(slip.transferred_at)}
                    </dd>
                  </div>
                </dl>

                <div className="flex items-center gap-4 border-t border-dashed border-slate-300 pt-4">
                  <QrBlock seed={slip.trans_ref} />
                  <div className="min-w-0 space-y-1">
                    <p className="text-2xs uppercase tracking-wide text-slate-500">
                      รหัสอ้างอิง / Transaction ref
                    </p>
                    <p className="tnum break-all font-mono text-xs font-semibold">
                      {slip.trans_ref}
                    </p>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      )}

      <p className="text-2xs leading-relaxed text-muted-foreground">
        <Trans
          i18nKey="domain.slipViewer.footnote"
          values={{ code: 'VerifiedSlip' }}
          components={{ code: <Code /> }}
        />
      </p>
    </div>
  );
}
